import React, { useState, useMemo, useRef } from 'react';
import {
    Typography, Card, Tabs, Input, DatePicker, Select, Table, Tag, Button, Space, Grid, Row, Col,
    Radio, Modal, InputNumber, message, ConfigProvider, Tooltip, Divider, Image
} from 'antd';
import {
    SearchOutlined, PrinterOutlined, EyeOutlined, RollbackOutlined, FileTextOutlined,
    SafetyCertificateOutlined, FilePdfOutlined
} from '@ant-design/icons';
import LayoutHome from '../../components/layout/LayoutHome';
import dayjs from 'dayjs';
import { useHistorialVentasGlobal, useNotasCredito } from '../../hooks/useVentas';
import axios from 'axios';
import { useAuth } from '../../aws/AuthContext';
import { useCaja } from '../../hooks/useCaja';
import { useQueryClient } from '@tanstack/react-query';
import es_ES from 'antd/locale/es_ES';
import { pdf } from '@react-pdf/renderer';
import { useConfig } from '../../context/ConfigContext';
import { FacturaFormal } from '../ventas/FacturaFormal';
import { generarTicketBluetooth } from '../../utils/impresionBluetooth';
import { useReactToPrint } from 'react-to-print';
import { TicketVenta } from '../ventas/TicketVenta';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const API_BASE_URL = process.env.REACT_APP_API_URL;

const Facturacion = () => {

    const componentRef = useRef(null);
    const [datosImpresion, setDatosImpresion] = useState(null);

    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: "Documento POS",
    });

    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { getJwtToken } = useAuth();
    const { config } = useConfig();

    const queryClient = useQueryClient();

    const { getCajaActiva } = useCaja();
    const cajaActiva = getCajaActiva?.data;

    // Estados para los filtros y modales
    const [activeTab, setActiveTab] = useState('ventas');
    const [busqueda, setBusqueda] = useState('');
    const [fechas, setFechas] = useState([dayjs().startOf('month'), dayjs().endOf('day')]);
    const [estadoPago, setEstadoPago] = useState('todos');
    const [filtroMetodoPago, setFiltroMetodoPago] = useState('todos');

    // Estados para el Modal de Devolución
    const [isModalDevolucionOpen, setIsModalDevolucionOpen] = useState(false);
    const [ventaSeleccionada, setVentaSeleccionada] = useState(null);
    const [itemsDevolucion, setItemsDevolucion] = useState([]);
    const [motivoDevolucion, setMotivoDevolucion] = useState('');
    const [cargandoDevolucion, setCargandoDevolucion] = useState(false);

    const [modalDetalleVisible, setModalDetalleVisible] = useState(false);
    const [docSeleccionado, setDocSeleccionado] = useState(null);

    // Convertimos a ISO para el backend
    const fechaInicioISO = fechas?.[0] ? fechas[0].startOf('day').toISOString() : null;
    const fechaFinISO = fechas?.[1] ? fechas[1].endOf('day').toISOString() : null;

    const { data: ventasReales = [], isLoading, refetch } = useHistorialVentasGlobal(
        fechaInicioISO,
        fechaFinISO,
        estadoPago
    );

    // Llamada al backend - Notas de Crédito
    const { data: notasCredito = [], isLoading: isLoadingNotas } = useNotasCredito();

    // Filtro local para el buscador
    const ventasFiltradas = useMemo(() => {
        if (!Array.isArray(ventasReales)) return [];
        return ventasReales.filter(v => {
            const matchBusqueda = v.numero_factura?.toLowerCase().includes(busqueda.toLowerCase()) ||
                v.cliente_nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
                v.cliente_documento?.toLowerCase().includes(busqueda.toLowerCase());

            const matchMetodo = filtroMetodoPago === 'todos' || v.metodo_pago?.toLowerCase() === filtroMetodoPago.toLowerCase();

            return matchBusqueda && matchMetodo;
        });
    }, [ventasReales, busqueda, filtroMetodoPago]);

    const aplicarFiltroRapido = (e) => {
        const tipo = e.target.value;
        const hoy = dayjs();
        if (tipo === 'hoy') {
            setFechas([hoy.startOf('day'), hoy.endOf('day')]);
        } else if (tipo === 'semana') {
            setFechas([hoy.startOf('week'), hoy.endOf('day')]);
        } else if (tipo === 'mes') {
            setFechas([hoy.startOf('month'), hoy.endOf('day')]);
        }
    };

    const abrirModalDevolucion = (venta) => {
        setVentaSeleccionada(venta);
        setMotivoDevolucion('');

        const itemsIniciales = (venta.items || []).map(item => {
            const yaDevuelto = item.cantidad_devuelta || 0;
            
            const precioPagado = Number(item.precio_con_descuento) > 0 
                ? Number(item.precio_con_descuento) 
                : Number(item.precio_unitario);

            const descuentoAplicadoPesos = Number(item.precio_unitario) - precioPagado;

            return {
                ...item,
                cantidad_disponible: item.cantidad - yaDevuelto,
                cantidad_devuelta_modal: 0,
                precio_neto_pagado: precioPagado,
                descuento_aplicado_pesos: Math.max(0, descuentoAplicadoPesos)
            };
        });
        setItemsDevolucion(itemsIniciales);
        setIsModalDevolucionOpen(true);
    };

    const seleccionarTodoParaDevolucion = () => {
        setItemsDevolucion(itemsDevolucion.map(item => ({
            ...item,
            cantidad_devuelta_modal: item.cantidad_disponible
        })));
    };

    const handleCantidadDevueltaChange = (index, nuevaCantidad) => {
        const nuevosItems = [...itemsDevolucion];
        const item = nuevosItems[index];

        if (nuevaCantidad > item.cantidad_disponible) {
            message.warning(`Solo le quedan ${item.cantidad_disponible} unidades disponibles para devolver.`);
            return;
        }
        item.cantidad_devuelta_modal = nuevaCantidad || 0;
        setItemsDevolucion(nuevosItems);
    };

    const procesarDevolucion = async () => {
        const itemsAProcesar = itemsDevolucion.filter(i => i.cantidad_devuelta_modal > 0);
        if (itemsAProcesar.length === 0) {
            return message.warning("Debe especificar al menos un producto con cantidad a devolver.");
        }
        if (!motivoDevolucion.trim()) {
            return message.warning("El motivo de la devolución es obligatorio.");
        }

        setCargandoDevolucion(true);
        try {
            const token = await getJwtToken();
            const payload = {
                venta_id: ventaSeleccionada.id,
                motivo: motivoDevolucion,
                sesion_caja_id_actual: cajaActiva?.id || null,
                items_devueltos: itemsAProcesar.map(i => ({
                    producto_id: i.producto_id,
                    variante_id: i.variante_id || null,
                    cantidad_devuelta: i.cantidad_devuelta_modal,
                    precio_unitario: i.precio_neto_pagado,
                    es_servicio: i.es_servicio || false
                }))
            };

            await axios.post(`${API_BASE_URL}/v1-notas-credito`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            message.success("Nota de crédito generada e inventario reingresado exitosamente.");
            setIsModalDevolucionOpen(false);

            refetch(); // Recargamos las ventas actuales
            queryClient.invalidateQueries(['productos']);
            queryClient.invalidateQueries(['notas-credito']);
            queryClient.invalidateQueries(['informes_dashboard']);
            queryClient.invalidateQueries(['informes_ventas']);

        } catch (error) {
            message.error(error.response?.data?.error || "Error al procesar la devolución.");
        } finally {
            setCargandoDevolucion(false);
        }
    };

    const columnasVentas = [
        {
            title: 'Fecha y Hora',
            dataIndex: 'fecha',
            key: 'fecha',
            width: 170,
            render: (text) => <Text>{dayjs(text).format('DD/MM/YYYY hh:mm A')}</Text>,
        },
        {
            title: 'Factura',
            dataIndex: 'numero_factura',
            key: 'numero_factura',
            width: 120,
            render: (text) => <Text strong>{text}</Text>,
        },
        {
            title: 'Cliente',
            key: 'cliente',
            width: 180,
            render: (_, record) => record.cliente_nombre ? <Text>{record.cliente_nombre}</Text> : <Text type="secondary">Consumidor Final</Text>
        },
        {
            title: 'Usuario',
            key: 'usuario',
            width: 160,
            render: (_, record) => <Text type="secondary">{record.cajero_nombre} {record.cajero_apellidos || ''}</Text>
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            width: 130,
            render: (val) => <Text strong>${Number(val).toLocaleString('es-CO')}</Text>,
        },
        {
            title: 'Estado',
            key: 'estado',
            width: 110,
            render: (_, record) => {
                if (record.status_pago === 'pagado') return <Tag color="success">PAGADO</Tag>;
                if (record.status_pago === 'pendiente') return <Tag color="warning">CRÉDITO</Tag>;
                return <Tag color="default">{record.status_pago}</Tag>;
            }
        },
        {
            title: 'Acciones',
            key: 'acciones',
            align: 'right',
            width: 160,
            render: (_, record) => {
                const yaFueDevueltaTotalmente = record.items?.every(item => (item.cantidad_devuelta || 0) >= item.cantidad);

                return (
                    <Space size="small">
                        <Tooltip title="Ver Detalles"><Button type="text" icon={<EyeOutlined />} onClick={() => abrirDetalles(record, 'venta')} /></Tooltip>
                        <Tooltip title="Imprimir Tirilla"><Button type="text" icon={<PrinterOutlined />} style={{ color: '#22BABB', background: '#e0f2fe' }} onClick={() => manejarImpresionTirilla(normalizarDatos(record, 'venta'))} /></Tooltip>
                        <Tooltip title="PDF Oficial"><Button type="text" icon={<FilePdfOutlined />} style={{ color: '#d32f2f', background: '#fce4e4' }} onClick={() => descargarPDFFormal(record, 'venta')} /></Tooltip>
                        <Button
                            type="primary"
                            danger
                            icon={<RollbackOutlined />}
                            onClick={() => abrirModalDevolucion(record)}
                            style={{ borderRadius: '8px' }}
                            disabled={yaFueDevueltaTotalmente}
                            title={yaFueDevueltaTotalmente ? "Factura devuelta totalmente" : "Devolver"}
                        >
                            Devolver
                        </Button>
                    </Space>
                );
            }
        }
    ];

    // Columnas para la tabla de Notas de Crédito
    const columnasNotasCredito = [
        {
            title: 'Fecha Devolución',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 170,
            render: (text) => <Text>{dayjs(text).format('DD/MM/YYYY hh:mm A')}</Text>,
        },
        {
            title: 'Nota de Crédito',
            dataIndex: 'numero_nota',
            key: 'numero_nota',
            width: 140,
            render: (text) => <Text strong style={{ color: '#ef4444' }}>{text}</Text>,
        },
        {
            title: 'Factura Afectada',
            dataIndex: 'numero_factura',
            key: 'numero_factura',
            width: 140,
            render: (text) => <Tag color="blue">{text}</Tag>,
        },
        {
            title: 'Motivo',
            dataIndex: 'motivo',
            key: 'motivo',
            width: 250,
        },
        {
            title: 'Usuario',
            key: 'usuario',
            width: 160,
            render: (_, record) => <Text type="secondary">{record.cajero_nombre} {record.cajero_apellidos || ''}</Text>
        },
        {
            title: 'Total Devuelto',
            dataIndex: 'total_devolucion',
            key: 'total_devolucion',
            width: 140,
            render: (val) => <Text strong style={{ color: '#ef4444' }}>${Number(val).toLocaleString('es-CO')}</Text>,
        },
        {
            title: 'Acciones', key: 'acciones', align: 'right', width: 170,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Ver Detalles"><Button type="text" icon={<EyeOutlined />} onClick={() => abrirDetalles(record, 'nota')} /></Tooltip>
                    <Tooltip title="Imprimir Tirilla"><Button type="text" icon={<PrinterOutlined />} style={{ color: '#22BABB', background: '#e0f2fe' }} onClick={() => manejarImpresionTirilla(normalizarDatos(record, 'nota'))} /></Tooltip>
                    <Tooltip title="PDF Oficial"><Button type="text" icon={<FilePdfOutlined />} style={{ color: '#d32f2f', background: '#fce4e4' }} onClick={() => descargarPDFFormal(record, 'nota')} /></Tooltip>
                </Space>
            )
        }
    ];

    const normalizarDatos = (record, tipo) => {
        if (tipo === 'venta') {
            return {
                ...record,
                tituloDocumento: 'FACTURA DE VENTA',
                comprobante_url: record.comprobante_url,
                efectivo_recibido: Number(record.efectivo_recibido || record.monto_recibido || record.efectivo || record.total),
                cambio_entregado: Number(record.cambio_entregado || record.cambio || 0),
                items: (record.items || []).map(i => ({
                    ...i,
                    precio_unitario: Number(i.precio_unitario || i.precio || 0),
                    subtotal: Number(i.subtotal || (i.precio_unitario * i.cantidad) || 0)
                }))
            };
        } else {
            return {
                tituloDocumento: 'NOTA DE CRÉDITO',
                id: record.id,
                numero_factura: record.numero_nota,
                fecha: record.created_at,
                cajero_nombre: record.cajero_nombre,
                cajero_apellidos: record.cajero_apellidos,
                cliente_nombre: record.cliente_nombre || 'CONSUMIDOR FINAL',
                cliente_documento: record.cliente_documento || '',
                metodo_pago: 'Devolución',
                total: record.total_devolucion,
                efectivo_recibido: record.total_devolucion,
                motivo: record.motivo,
                items: record.items || []
            };
        }
    };

    const calcularMatematicas = (doc) => {
        if (!doc) return { subtotal: 0, descuento: 0, total: 0 };
        let sumaDescuentos = 0; let sumaSubtotal = 0;
        (doc.items || []).forEach(item => {
            const precioBase = Number(item.precio_unitario) || 0;
            const cantidad = Number(item.cantidad) || 1;
            const porcentajeDescuento = Number(item.descuento) || 0;
            sumaSubtotal += (precioBase * cantidad);
            sumaDescuentos += ((precioBase * cantidad) * (porcentajeDescuento / 100));
        });
        return { subtotal: sumaSubtotal, descuento: Math.round(sumaDescuentos), total: doc.total };
    };

    const abrirDetalles = (record, tipo) => {
        setDocSeleccionado(normalizarDatos(record, tipo));
        setModalDetalleVisible(true);
    };

    const formatearDatosParaImpresion = (doc) => {
        const math = calcularMatematicas(doc);
        let efectivoFinal = Number(doc.efectivo_recibido);
        if (doc.metodo_pago?.toLowerCase() === 'mixto') {
            efectivoFinal = Number(doc.monto_efectivo || 0);
        }

        return {
            tituloDocumento: doc.tituloDocumento,
            carrito: (doc.items || []).map(item => ({
                codigo_barras: item.codigo_barras || 'S/N', nombre: item.nombre, numItems: item.cantidad || 1,
                precioVenta: Number(item.precio_unitario) || 0, descuento: Number(item.descuento) || 0
            })),
            totales: { subtotal: math.subtotal, impuestos: 0, total: math.total, totalDescuento: math.descuento },
            ventaId: doc.id, numeroFactura: doc.numero_factura, fechaVenta: doc.fecha,
            clienteNombre: doc.cliente_nombre, clienteDocumento: doc.cliente_documento,
            cajero: `${doc.cajero_nombre || ''} ${doc.cajero_apellidos || ''}`.trim(),
            metodoPago: doc.metodo_pago,
            efectivoRecibido: efectivoFinal,
            cambioEntregado: Number(doc.cambio_entregado || 0),
            montoTransferencia: Number(doc.monto_transferencia || 0)
        };
    };

    const infoNegocioBase = {
        nombre: config?.nombre, nombre_legal: config?.nombre_legal, nit: config?.identificacion_fiscal,
        direccion: config?.direccion, telefono: config?.telefono, logo_url: config?.logo_url
    };

    const descargarPDFFormal = async (record, tipo) => {
        const doc = normalizarDatos(record, tipo);
        const datosPrint = formatearDatosParaImpresion(doc);
        const nuevaPestana = window.open('', '_blank');
        if (nuevaPestana) nuevaPestana.document.write('Generando documento oficial...');
        try {
            const blob = await pdf(<FacturaFormal {...datosPrint} infoNegocio={infoNegocioBase} />).toBlob();
            if (nuevaPestana) nuevaPestana.location.href = URL.createObjectURL(blob);
        } catch (error) {
            if (nuevaPestana) nuevaPestana.close();
            message.error("Error al generar PDF.");
        }
    };

    const manejarImpresionTirilla = async (doc) => {
        const datosPrint = formatearDatosParaImpresion(doc);
        const tipoImpresora = config?.impresora_tipo || config?.settings?.impresora_tipo || '80mm';
        const conexionImpresora = config?.settings?.conexion_impresora || 'sistema';

        if (conexionImpresora === 'bluetooth') {
            message.loading({ content: 'Conectando con impresora Bluetooth...', key: 'imprimiendoBT' });
            try {
                const device = await navigator.bluetooth.requestDevice({
                    acceptAllDevices: true,
                    optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
                });
                const server = await device.gatt.connect();
                const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
                const characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');
                const payload = generarTicketBluetooth(datosPrint, infoNegocioBase, tipoImpresora);
                for (let i = 0; i < payload.length; i += 100) {
                    await characteristic.writeValue(payload.slice(i, i + 100));
                    await new Promise(resolve => setTimeout(resolve, 150));
                }
                device.gatt.disconnect();
                message.success({ content: 'Impresión exitosa', key: 'imprimiendoBT' });
            } catch (error) {
                message.error({ content: "Fallo la impresión Bluetooth.", key: 'imprimiendoBT' });
            }
        } else {
            // Impresión por sistema utilizando react-to-print y el componente TicketVenta
            setDatosImpresion(datosPrint);
            setTimeout(() => {
                handlePrint();
            }, 300);
        }
    };

    return (
        <ConfigProvider locale={es_ES}>
            <LayoutHome pageSeleccionada="facturacion">
                <div style={{ padding: isMobile ? '10px' : '15px 20px', maxWidth: '1400px', margin: '0 auto', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                    <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                        <div style={{ background: '#e0f2fe', padding: '10px', borderRadius: '12px', color: '#0284c7' }}>
                            <FileTextOutlined style={{ fontSize: '24px' }} />
                        </div>
                        <div>
                            <Title level={3} style={{ margin: 0 }}>Facturación y Documentos</Title>
                            <Text type="secondary">Historial global de ventas, notas de crédito y documentos electrónicos.</Text>
                        </div>
                    </div>

                    <Card
                        style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
                        bodyStyle={{ padding: '0px', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
                        styles={{ body: { padding: '0px', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' } }}
                    >
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            style={{ padding: '0 20px', flexShrink: 0 }}
                            items={[
                                { key: 'ventas', label: <span><FileTextOutlined /> Historial de Ventas POS</span> },
                                { key: 'notas_credito', label: <span><RollbackOutlined /> Notas de Crédito (Devoluciones)</span> },
                                { key: 'dian', label: <span title='Próximamente'><SafetyCertificateOutlined /> Facturación Electrónica DIAN</span>, disabled: true }
                            ]}
                        />

                        <div style={{ padding: '15px 20px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                            {activeTab === 'ventas' && (
                                <>
                                    <Row gutter={[12, 12]} align="bottom" style={{ marginBottom: '15px', background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', flexShrink: 0 }}>
                                        <Col xs={24} md={5}>
                                            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>Buscar factura o cliente</Text>
                                                <Input
                                                    placeholder="Ej: POS-123, Juan..."
                                                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                                    style={{ height: '40px', borderRadius: '8px' }}
                                                    value={busqueda}
                                                    allowClear
                                                    onChange={e => setBusqueda(e.target.value)}
                                                />
                                            </Space>
                                        </Col>

                                        <Col xs={24} md={4}>
                                            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>Filtros rápidos</Text>
                                                <Radio.Group defaultValue="mes" onChange={aplicarFiltroRapido} style={{ width: '100%', display: 'flex' }}>
                                                    <Radio.Button value="hoy" style={{ flex: 1, textAlign: 'center', height: '40px', lineHeight: '40px' }}>Hoy</Radio.Button>
                                                    <Radio.Button value="semana" style={{ flex: 1, textAlign: 'center', height: '40px', lineHeight: '40px' }}>Semana</Radio.Button>
                                                    <Radio.Button value="mes" style={{ flex: 1, textAlign: 'center', height: '40px', lineHeight: '40px' }}>Mes</Radio.Button>
                                                </Radio.Group>
                                            </Space>
                                        </Col>

                                        <Col xs={12} md={3}>
                                            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>Desde</Text>
                                                <DatePicker
                                                    style={{ width: '100%', height: '40px', borderRadius: '8px' }}
                                                    value={fechas[0]}
                                                    onChange={(val) => setFechas([val || dayjs(), fechas[1]])}
                                                    disabledDate={(current) => current && current > fechas[1]}
                                                    format="DD/MM/YYYY"
                                                    allowClear={false}
                                                />
                                            </Space>
                                        </Col>

                                        <Col xs={12} md={3}>
                                            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>Hasta</Text>
                                                <DatePicker
                                                    style={{ width: '100%', height: '40px', borderRadius: '8px' }}
                                                    value={fechas[1]}
                                                    onChange={(val) => setFechas([fechas[0], val || dayjs()])}
                                                    disabledDate={(current) => current && current < fechas[0].startOf('day')}
                                                    format="DD/MM/YYYY"
                                                    allowClear={false}
                                                />
                                            </Space>
                                        </Col>

                                        <Col xs={12} md={4}>
                                            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>Estado del pago</Text>
                                                <Select
                                                    style={{ width: '100%', height: '40px' }}
                                                    value={estadoPago}
                                                    onChange={setEstadoPago}
                                                    options={[
                                                        { value: 'todos', label: 'Todos los estados' },
                                                        { value: 'pagado', label: 'Solo Pagados' },
                                                        { value: 'pendiente', label: 'Solo Crédito (Fiado)' }
                                                    ]}
                                                />
                                            </Space>
                                        </Col>

                                        <Col xs={12} md={5}>
                                            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>Método de Pago</Text>
                                                <Select
                                                    style={{ width: '100%', height: '40px' }}
                                                    value={filtroMetodoPago}
                                                    onChange={setFiltroMetodoPago}
                                                    options={[
                                                        { value: 'todos', label: 'Todos los métodos' },
                                                        { value: 'efectivo', label: 'Efectivo' },
                                                        { value: 'transferencia', label: 'Transferencia' },
                                                        { value: 'mixto', label: 'Mixto' },
                                                        { value: 'credito', label: 'Crédito' }
                                                    ]}
                                                />
                                            </Space>
                                        </Col>
                                    </Row>

                                    <Table
                                        columns={columnasVentas}
                                        dataSource={ventasFiltradas}
                                        rowKey="id"
                                        loading={isLoading}
                                        scroll={{ x: 'max-content', y: isMobile ? 'calc(100vh - 490px)' : 'calc(100vh - 420px)' }}
                                        pagination={{ pageSize: 50, showSizeChanger: false }}
                                        style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
                                    />
                                </>
                            )}

                            {activeTab === 'notas_credito' && (
                                <Table
                                    columns={columnasNotasCredito}
                                    dataSource={notasCredito}
                                    rowKey="id"
                                    loading={isLoadingNotas}
                                    scroll={{ x: 'max-content', y: isMobile ? 'calc(100vh - 400px)' : 'calc(100vh - 310px)' }}
                                    pagination={{ pageSize: 50, showSizeChanger: false }}
                                    style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
                                />
                            )}
                        </div>
                    </Card>
                </div>

                <Modal
                    title={null}
                    open={isModalDevolucionOpen}
                    onCancel={() => setIsModalDevolucionOpen(false)}
                    footer={null}
                    centered
                    width={600}
                    styles={{ mask: { background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(5px)' } }}
                >
                    {ventaSeleccionada && (
                        <div>
                            <div style={{ textAlign: 'center', padding: '10px 0 15px' }}>
                                <div style={{ background: '#fef2f2', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                                    <RollbackOutlined style={{ fontSize: '24px', color: '#ef4444' }} />
                                </div>
                                <Title level={4} style={{ margin: 0 }}>Procesar Devolución / Anulación</Title>
                                <Text type="secondary">Factura: <strong style={{ color: '#0f172a' }}>{ventaSeleccionada.numero_factura}</strong></Text>
                            </div>

                            <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '10px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <Text type="secondary">Cliente:</Text>
                                    <Text strong>{ventaSeleccionada.cliente_nombre || 'Consumidor Final'}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary">Método de Pago Original:</Text>
                                    <Tag color="blue">{ventaSeleccionada.metodo_pago}</Tag>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <Text strong style={{ fontSize: '13px' }}>Productos a devolver (Indique cantidad):</Text>
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={seleccionarTodoParaDevolucion}
                                    style={{ padding: 0, fontWeight: 'bold' }}
                                >
                                    Seleccionar todos
                                </Button>
                            </div>

                            <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px' }}>
                                {itemsDevolucion.map((item, index) => (
                                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 4px', borderBottom: '1px solid #f1f5f9' }}>
                                        <div style={{ flex: 1, paddingRight: '10px' }}>
                                            <Text strong style={{ display: 'block', fontSize: '13px' }}>{item.nombre}</Text>
                                            <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>
                                                Comprados: {item.cantidad} | Ya devueltos: {item.cantidad_devuelta || 0} | Disp: {item.cantidad_disponible}
                                            </Text>
                                            <Text type="secondary" style={{ fontSize: '11px', display: 'block', color: Number(item.descuento) > 0 ? '#16a34a' : '#64748b' }}>
                                                Precio pagado c/u: ${Number(item.precio_neto_pagado).toLocaleString('es-CO')}
                                                {Number(item.descuento) > 0 ? ` (-${item.descuento}% desc.)` : ''}
                                            </Text>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>Devolver:</Text>
                                            <InputNumber
                                                min={0}
                                                max={item.cantidad_disponible}
                                                value={item.cantidad_devuelta_modal}
                                                onChange={(val) => handleCantidadDevueltaChange(index, val)}
                                                controls={false}
                                                disabled={item.cantidad_disponible === 0}
                                                style={{ width: '60px' }}
                                                styles={{ input: { textAlign: 'center', fontWeight: 'bold' } }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '6px' }}>Motivo de la Devolución / Anulación *</Text>
                                <Input.TextArea
                                    rows={2}
                                    placeholder="Ej: Producto defectuoso, cambio de opinión del cliente..."
                                    value={motivoDevolucion}
                                    onChange={(e) => setMotivoDevolucion(e.target.value)}
                                />
                            </div>

                            <Button
                                type="primary"
                                danger
                                block
                                size="large"
                                loading={cargandoDevolucion}
                                onClick={procesarDevolucion}
                                style={{ background: '#22BABB', height: '50px', borderRadius: '10px', fontWeight: 'bold' }}
                            >
                                CONFIRMAR Y GENERAR NOTA DE CRÉDITO
                            </Button>
                        </div>
                    )}
                </Modal>

                <Modal
                    title={<Space><FileTextOutlined style={{ color: '#22BABB' }} /><Text strong style={{ fontSize: '16px' }}>DETALLE DEL DOCUMENTO</Text></Space>}
                    open={modalDetalleVisible} onCancel={() => setModalDetalleVisible(false)} width={450} centered
                    footer={[
                        <Button key="print" icon={<PrinterOutlined />} onClick={() => manejarImpresionTirilla(docSeleccionado)} style={{ height: '45px', fontWeight: 'bold', borderRadius: '8px', borderColor: '#22BABB', color: '#22BABB' }}>
                            IMPRIMIR TIRILLA
                        </Button>,
                        <Button key="pdf" type="primary" icon={<FilePdfOutlined />} onClick={() => descargarPDFFormal(docSeleccionado, docSeleccionado.tituloDocumento === 'FACTURA DE VENTA' ? 'venta' : 'nota')} style={{ background: '#22BABB', height: '45px', fontWeight: 'bold', borderRadius: '8px', border: 'none' }}>
                            DESCARGAR PDF
                        </Button>
                    ]}
                    styles={{ body: { padding: 0 } }}
                >
                    {docSeleccionado && (() => {
                        const m = calcularMatematicas(docSeleccionado);
                        return (
                            <div style={{ padding: '25px', backgroundColor: '#fff', maxHeight: '70vh', overflowY: 'auto' }}>
                                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                                    <Title level={4} style={{ margin: 0, color: '#0f172a' }}>{docSeleccionado.tituloDocumento}</Title>
                                    <Title level={5} style={{ margin: '4px 0', color: '#22BABB' }}>#{docSeleccionado.numero_factura}</Title>
                                    <Text type="secondary" style={{ fontSize: '13px' }}>{dayjs(docSeleccionado.fecha).format('DD/MM/YYYY hh:mm A')}</Text>
                                </div>

                                <div style={{ fontSize: '13px', color: '#475569', background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                        <Text strong>Cajero:</Text><Text>{docSeleccionado.cajero_nombre} {docSeleccionado.cajero_apellidos}</Text>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                        <Text strong>Cliente:</Text><Text>{docSeleccionado.cliente_nombre}</Text>
                                    </div>
                                    {docSeleccionado.motivo && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Text strong>Motivo:</Text>
                                            <Text style={{ maxWidth: '65%', textAlign: 'right' }}>{docSeleccionado.motivo}</Text>
                                        </div>
                                    )}
                                </div>

                                <Divider style={{ margin: '20px 0' }} />

                                <div style={{ marginBottom: 15 }}>
                                    <Text strong style={{ fontSize: '12px', color: '#94a3b8', display: 'block', marginBottom: '10px', textTransform: 'uppercase' }}>Productos Procesados</Text>
                                    {docSeleccionado.items?.map((item, i) => (
                                        <div key={i} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #f1f5f9' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                <Text strong style={{ color: '#1e293b' }}>{item.nombre?.toUpperCase()}</Text>
                                                <Text strong style={{ color: '#0f172a' }}>${Number(item.subtotal).toLocaleString('es-CO')}</Text>
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                {item.cantidad} Und x ${Number(item.precio_unitario).toLocaleString('es-CO')}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div style={{ background: '#f1f5f9', padding: '16px', borderRadius: '10px', marginTop: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                        <Text style={{ color: '#64748b' }}>Subtotal:</Text>
                                        <Text strong>${m.subtotal.toLocaleString('es-CO')}</Text>
                                    </div>
                                    {m.descuento > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                            <Text style={{ color: '#16a34a' }}>Descuento:</Text>
                                            <Text strong style={{ color: '#16a34a' }}>-${m.descuento.toLocaleString('es-CO')}</Text>
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #cbd5e1' }}>
                                        <Text strong style={{ color: '#0f172a' }}>TOTAL:</Text>
                                        <Text strong style={{ color: '#22BABB' }}>${Number(docSeleccionado.total).toLocaleString('es-CO')}</Text>
                                    </div>

                                    {docSeleccionado.tituloDocumento === 'FACTURA DE VENTA' && (
                                        <>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #cbd5e1' }}>
                                                <Text style={{ color: '#64748b' }}>Efectivo Recibido:</Text>
                                                <Text strong>${Number(docSeleccionado.efectivo_recibido).toLocaleString('es-CO')}</Text>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                                                <Text style={{ color: '#64748b' }}>Cambio Entregado:</Text>
                                                <Text strong>${Number(docSeleccionado.cambio_entregado).toLocaleString('es-CO')}</Text>
                                            </div>
                                            {docSeleccionado.comprobante_url && (
                                                <div style={{ marginTop: '20px', textAlign: 'center', background: '#fff', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                                    <Text strong style={{ display: 'block', marginBottom: '10px', color: '#475569' }}>Soporte de Pago (Transferencia / Mixto)</Text>
                                                    <Image
                                                        src={docSeleccionado.comprobante_url}
                                                        alt="Soporte de Pago"
                                                        style={{ maxHeight: '200px', borderRadius: '6px', objectFit: 'contain' }}
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )
                    })()}
                </Modal>

                {/* COMPONENTE OCULTO PARA IMPRESIÓN POR SISTEMA */}
                <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
                    <div ref={componentRef} style={{ display: 'flex', justifyContent: 'center', minHeight: '100vh', padding: '5px 2px 20px 2px', backgroundColor: '#F0F2F4' }}>
                        {datosImpresion && (
                            <TicketVenta
                                carrito={datosImpresion.carrito}
                                totales={datosImpresion.totales}
                                ventaId={datosImpresion.ventaId}
                                numeroFactura={datosImpresion.numeroFactura}
                                fechaVenta={datosImpresion.fechaVenta}
                                clienteNombre={datosImpresion.clienteNombre}
                                clienteDocumento={datosImpresion.clienteDocumento}
                                cajero={datosImpresion.cajero || "Cajero(a)"}
                                metodoPago={datosImpresion.metodoPago}
                                cambioEntregado={datosImpresion.cambioEntregado}
                                efectivoRecibido={datosImpresion.efectivoRecibido}
                                montoTransferencia={datosImpresion.montoTransferencia}
                                infoNegocio={{
                                    nombre: config?.nombre,
                                    nombre_legal: config?.nombre_legal,
                                    nit: config?.identificacion_fiscal,
                                    direccion: config?.direccion,
                                    telefono: config?.telefono,
                                    anchoTicket: config?.impresora_tipo || config?.settings?.impresora_tipo || '57mm'
                                }}
                            />
                        )}
                    </div>
                </div>
            </LayoutHome>
        </ConfigProvider>
    );
};

export default Facturacion;